import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Building2, 
  MapPin, 
  Calendar, 
  Users, 
  DollarSign,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Calculator,
  Clock,
  Briefcase,
  Upload,
  Image,
  FileText,
  X,
  Shield,
  Plus,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientRequestProject() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location_address: "",
    location_city: "",
    project_type: searchParams.get("type") || "workforce",
    service_type: "full_service",
    complexity: "medium",
    estimated_duration_days: 5,
    crew_size: 4,
    proposed_budget: 100000,
    start_date: "",
    end_date: "",
    special_requirements: "",
    is_private_client: false
  });

  // File uploads state
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);

  // SJA (Safe Job Analysis) state
  const [sjaForm, setSjaForm] = useState({
    job_description: "",
    hazards: [], // Array of { id, probability, consequence, responsible, measures }
    ppe_required: ["helmet", "safety_shoes", "hi_vis"],
    additional_measures: "",
    emergency_contact: "",
    nearest_hospital: "",
    first_aid_location: "",
    custom_hazards: [] // Array of { description, probability, consequence, responsible, measures }
  });

  // Predefined hazards
  const hazardOptions = [
    { id: "fall_height", label: t('sja.hazards.fallHeight'), icon: "⚠️" },
    { id: "falling_objects", label: t('sja.hazards.fallingObjects'), icon: "📦" },
    { id: "weather", label: t('sja.hazards.weather'), icon: "🌧️" },
    { id: "traffic", label: t('sja.hazards.traffic'), icon: "🚗" },
    { id: "electrical", label: t('sja.hazards.electrical'), icon: "⚡" },
    { id: "heavy_lifting", label: t('sja.hazards.heavyLifting'), icon: "💪" },
    { id: "confined_space", label: t('sja.hazards.confinedSpace'), icon: "🚪" },
    { id: "noise", label: t('sja.hazards.noise'), icon: "🔊" },
    { id: "chemicals", label: t('sja.hazards.chemicals'), icon: "☢️" },
    { id: "other", label: t('sja.hazards.other'), icon: "➕" }
  ];

  // PPE options with icons
  const ppeOptions = [
    { id: "helmet", label: t('sja.ppe.helmet'), icon: "🪖", color: "text-yellow-400" },
    { id: "harness", label: t('sja.ppe.harness'), icon: "🦺", color: "text-red-400" },
    { id: "gloves", label: t('sja.ppe.gloves'), icon: "🧤", color: "text-blue-300" },
    { id: "safety_shoes", label: t('sja.ppe.safetyShoes'), icon: "👢", color: "text-red-400" },
    { id: "hi_vis", label: t('sja.ppe.hiVis'), icon: "🦺", color: "text-yellow-400" },
    { id: "safety_glasses", label: t('sja.ppe.safetyGlasses'), icon: "🥽", color: "text-blue-400" },
    { id: "ear_protection", label: t('sja.ppe.earProtection'), icon: "🎧", color: "text-gray-300" },
    { id: "dust_mask", label: t('sja.ppe.dustMask'), icon: "😷", color: "text-orange-400" }
  ];

  // Risk level calculation
  const getRiskLevel = (probability, consequence) => {
    const probVal = { low: 1, medium: 2, high: 3 }[probability] || 2;
    const consVal = { low: 1, medium: 2, high: 3 }[consequence] || 2;
    const risk = probVal * consVal;
    if (risk <= 2) return { level: "low", label: "Lav risiko", color: "bg-green-500" };
    if (risk <= 4) return { level: "medium", label: "Medium risiko", color: "bg-yellow-500" };
    return { level: "high", label: "Høy risiko", color: "bg-red-500" };
  };

  useEffect(() => {
    // Only auto-calculate end_date when start_date or duration changes (not when end_date is manually set)
    if (formData.start_date && formData.estimated_duration_days && !formData._endDateManuallySet) {
      const start = new Date(formData.start_date);
      // Duration includes the start day, so we add (duration - 1) days
      // Example: 6 days starting Feb 2 = Feb 2,3,4,5,6,7 = ends Feb 7
      start.setDate(start.getDate() + formData.estimated_duration_days - 1);
      setFormData(prev => ({ ...prev, end_date: start.toISOString().split("T")[0] }));
    }
  }, [formData.start_date, formData.estimated_duration_days]);

  // Handle end date change - calculate duration from dates
  const handleEndDateChange = (newEndDate) => {
    if (formData.start_date && newEndDate) {
      const start = new Date(formData.start_date);
      const end = new Date(newEndDate);
      const diffTime = end - start;
      // Duration includes both start and end day
      // Example: Feb 2 to Feb 7 = 6 days (2,3,4,5,6,7)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      
      if (diffDays > 0) {
        setFormData(prev => ({ 
          ...prev, 
          end_date: newEndDate, 
          estimated_duration_days: diffDays,
          _endDateManuallySet: true 
        }));
      }
    }
  };

  // Handle duration change - reset manual flag
  const handleDurationChange = (newDuration) => {
    setFormData(prev => ({ 
      ...prev, 
      estimated_duration_days: newDuration,
      _endDateManuallySet: false 
    }));
  };

  useEffect(() => {
    if (formData.proposed_budget > 0) {
      calculateFees(formData.proposed_budget);
    }
  }, [formData.proposed_budget]);

  const calculateFees = async (amount) => {
    try {
      const res = await fetch(`${API_URL}/api/holding/fees/calculate?gross_amount=${amount}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setFeeBreakdown(await res.json());
      }
    } catch (err) {
      console.error("Error calculating fees:", err);
    }
  };

  // File upload handlers
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhotos(true);
    const uploadedPhotos = [];
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} er ikke et gyldig bilde`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} er for stort (maks 10MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          uploadedPhotos.push({ url: data.url, name: file.name });
          toast.success(`${file.name} lastet opp!`);
        } else {
          const error = await res.json();
          toast.error(`Feil: ${error.detail || 'Upload feilet'}`);
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error(`Kunne ikke laste opp ${file.name}`);
      }
    }
    
    setPhotos(prev => [...prev, ...uploadedPhotos]);
    setUploadingPhotos(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingDocs(true);
    const uploadedDocs = [];
    
    const allowedExtensions = ['.pdf', '.ifc', '.step', '.stp', '.dwg', '.dxf'];
    
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        toast.error(`${file.name} - kun PDF, IFC, DWG filer er tillatt`);
        continue;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} er for stort (maks 50MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${API_URL}/api/upload/document`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          uploadedDocs.push({ url: data.url, name: file.name, type: ext });
          toast.success(`${file.name} lastet opp!`);
        } else {
          const error = await res.json();
          toast.error(`Feil: ${error.detail || 'Upload feilet'}`);
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error(`Kunne ikke laste opp ${file.name}`);
      }
    }
    
    setDocuments(prev => [...prev, ...uploadedDocs]);
    setUploadingDocs(false);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // SJA handlers
  const handleSjaChange = (field, value) => {
    setSjaForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleHazard = (hazardId) => {
    setSjaForm(prev => {
      const existingIndex = prev.hazards.findIndex(h => h.id === hazardId);
      if (existingIndex >= 0) {
        // Remove hazard
        return {
          ...prev,
          hazards: prev.hazards.filter(h => h.id !== hazardId)
        };
      } else {
        // Add hazard with default values
        return {
          ...prev,
          hazards: [...prev.hazards, { 
            id: hazardId, 
            probability: "medium", 
            consequence: "medium", 
            responsible: "", 
            measures: "" 
          }]
        };
      }
    });
  };

  const updateHazardDetails = (hazardId, field, value) => {
    setSjaForm(prev => ({
      ...prev,
      hazards: prev.hazards.map(h => 
        h.id === hazardId ? { ...h, [field]: value } : h
      )
    }));
  };

  const isHazardSelected = (hazardId) => {
    return sjaForm.hazards.some(h => h.id === hazardId);
  };

  const getHazardDetails = (hazardId) => {
    return sjaForm.hazards.find(h => h.id === hazardId) || { probability: "medium", consequence: "medium", measures: "", responsible: "" };
  };

  const togglePPE = (ppeId) => {
    setSjaForm(prev => ({
      ...prev,
      ppe_required: prev.ppe_required.includes(ppeId)
        ? prev.ppe_required.filter(p => p !== ppeId)
        : [...prev.ppe_required, ppeId]
    }));
  };

  const addCustomHazard = () => {
    const hazard = prompt(t('sja.addCustomHazard') || "Legg til egen fare:");
    if (hazard && hazard.trim()) {
      setSjaForm(prev => ({
        ...prev,
        custom_hazards: [...prev.custom_hazards, { 
          description: hazard.trim(), 
          probability: "medium", 
          consequence: "medium", 
          responsible: "", 
          measures: "" 
        }]
      }));
    }
  };

  const updateCustomHazardField = (index, field, value) => {
    setSjaForm(prev => ({
      ...prev,
      custom_hazards: prev.custom_hazards.map((h, i) => 
        i === index ? { ...h, [field]: value } : h
      )
    }));
  };

  const removeCustomHazard = (index) => {
    setSjaForm(prev => ({
      ...prev,
      custom_hazards: prev.custom_hazards.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1: return formData.title && formData.description && formData.project_type;
      case 2: return formData.location_address && formData.location_city;
      case 3: return formData.crew_size > 0 && formData.estimated_duration_days > 0 && formData.start_date;
      case 4: return true; // Files are optional
      case 5: return true; // SJA is optional but encouraged
      case 6: return formData.proposed_budget > 0;
      default: return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(step)) {
      toast.error(t('common.fillRequired') || "Vennligst fyll ut alle obligatoriske felt");
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) {
      toast.error(t('common.fillRequired') || "Vennligst fyll ut alle obligatoriske felt");
      return;
    }

    setLoading(true);
    try {
      // Build submission data with files and SJA
      const submissionData = {
        ...formData,
        project_images: photos.map(p => ({ url: p.url, name: p.name || 'Photo' })),
        project_documents: documents.map(d => ({ url: d.url, name: d.name, type: d.type })),
        sja_form: {
          ...sjaForm,
          created_at: new Date().toISOString(),
          created_by: user?.name || "Client"
        }
      };

      const res = await fetch(`${API_URL}/api/projects-v3/client/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submissionData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Kunne ikke opprette prosjekt");
      }

      const data = await res.json();
      toast.success(t('requestProject.success.message') || "Prosjektforespørsel sendt til AllStillas Holding!");
      navigate(`/client/projects-v3/${data.project_id}`);
      
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getMinDate = () => {
    const today = new Date();
    // Allow selecting today's date
    return today.toISOString().split("T")[0];
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6" data-testid="client-request-project">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('requestProject.back')}
          </Button>
          <h1 className="text-2xl font-bold text-white">{t('requestProject.title')}</h1>
          <p className="text-gray-400 mt-1">{t('requestProject.subtitle')}</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {[
            { num: 1, label: t('requestProject.steps.info') || "Info", icon: Briefcase }, 
            { num: 2, label: t('requestProject.steps.location') || "Sted", icon: MapPin }, 
            { num: 3, label: t('requestProject.steps.time') || "Tid", icon: Calendar },
            { num: 4, label: t('requestProject.steps.files') || "Filer", icon: Upload },
            { num: 5, label: "SJA", icon: Shield },
            { num: 6, label: t('requestProject.steps.price') || "Pris", icon: DollarSign }
          ].map((s, i) => (
            <div key={s.num} className="flex items-center flex-shrink-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
                step > s.num ? "bg-green-500 text-white" : step === s.num ? "bg-[#23F7DD] text-black" : "bg-[#2a2a4a] text-gray-400"
              }`}>
                {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
              </div>
              <span className={`ml-1 text-xs hidden lg:block ${step >= s.num ? "text-white" : "text-gray-500"}`}>{s.label}</span>
              {i < 5 && <div className={`w-6 md:w-12 h-0.5 mx-1 ${step > s.num ? "bg-green-500" : "bg-[#2a2a4a]"}`} />}
            </div>
          ))}
        </div>

        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardContent className="p-6">
            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <Briefcase className="w-6 h-6 text-[#23F7DD]" />
                  <div>
                    <h2 className="text-xl font-bold text-white">{t('requestProject.projectInfo.title')}</h2>
                    <p className="text-gray-400 text-sm">{t('requestProject.projectInfo.subtitle')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-300">{t('requestProject.projectInfo.projectTitle')} *</Label>
                    <Input value={formData.title} onChange={(e) => handleInputChange("title", e.target.value)}
                      placeholder={t('requestProject.projectInfo.projectTitlePlaceholder')} className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('requestProject.projectInfo.serviceType')} *</Label>
                    <Select value={formData.project_type} onValueChange={(v) => handleInputChange("project_type", v)}>
                      <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                        <SelectItem value="workforce">{t('requestProject.projectInfo.workforce')}</SelectItem>
                        <SelectItem value="stillastjenester">{t('requestProject.projectInfo.fullService')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('requestProject.projectInfo.service')} *</Label>
                    <Select value={formData.service_type} onValueChange={(v) => handleInputChange("service_type", v)}>
                      <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                        <SelectItem value="assembly">{t('requestProject.projectInfo.assemblyOnly')}</SelectItem>
                        <SelectItem value="disassembly">{t('requestProject.projectInfo.disassemblyOnly')}</SelectItem>
                        <SelectItem value="modification">{t('requestProject.projectInfo.modification')}</SelectItem>
                        <SelectItem value="full_service">{t('requestProject.projectInfo.fullServiceOption')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('requestProject.projectInfo.description')} *</Label>
                    <Textarea value={formData.description} onChange={(e) => handleInputChange("description", e.target.value)}
                      placeholder={t('requestProject.projectInfo.descriptionPlaceholder')} className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1 min-h-[120px]" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-6 h-6 text-[#23F7DD]" />
                  <div><h2 className="text-xl font-bold text-white">{t('requestProject.locationInfo.title')}</h2><p className="text-gray-400 text-sm">{t('requestProject.locationInfo.subtitle')}</p></div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-300">{t('requestProject.locationInfo.address')} *</Label>
                    <Input value={formData.location_address} onChange={(e) => handleInputChange("location_address", e.target.value)}
                      placeholder={t('requestProject.locationInfo.addressPlaceholder')} className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('requestProject.locationInfo.city')} *</Label>
                    <Input value={formData.location_city} onChange={(e) => handleInputChange("location_city", e.target.value)}
                      placeholder={t('requestProject.locationInfo.cityPlaceholder')} className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('requestProject.timeInfo.specialRequirements')}</Label>
                    <Textarea value={formData.special_requirements} onChange={(e) => handleInputChange("special_requirements", e.target.value)}
                      placeholder={t('requestProject.timeInfo.specialRequirementsPlaceholder')} className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-6 h-6 text-[#23F7DD]" />
                  <div><h2 className="text-xl font-bold text-white">{t('requestProject.timeInfo.title')}</h2><p className="text-gray-400 text-sm">{t('requestProject.timeInfo.subtitle')}</p></div>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">{t('requestProject.timeInfo.startDate')} *</Label>
                      <Input type="date" value={formData.start_date} min={getMinDate()} onChange={(e) => handleInputChange("start_date", e.target.value)}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-300">{t('requestProject.timeInfo.endDate')} *</Label>
                      <Input 
                        type="date" 
                        value={formData.end_date} 
                        min={formData.start_date || getMinDate()}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" 
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('requestProject.timeInfo.duration')}</Label>
                    <Input type="number" min={1} value={formData.estimated_duration_days}
                      onChange={(e) => handleDurationChange(parseInt(e.target.value) || 1)}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" />
                    <p className="text-gray-500 text-xs mt-1">{t('requestProject.timeInfo.durationHint')}</p>
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('requestProject.timeInfo.crewSize')} *</Label>
                    <Input type="number" min={1} max={50} value={formData.crew_size}
                      onChange={(e) => handleInputChange("crew_size", parseInt(e.target.value) || 1)}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4 - Files Upload */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <Upload className="w-6 h-6 text-[#23F7DD]" />
                  <div>
                    <h2 className="text-xl font-bold text-white">{t('requestProject.files.title') || "Prosjektfiler"}</h2>
                    <p className="text-gray-400 text-sm">{t('requestProject.files.subtitle') || "Last opp bilder og dokumenter"}</p>
                  </div>
                </div>
                
                {/* Photos Upload */}
                <div className="space-y-3">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Image className="w-4 h-4" /> {t('requestProject.files.photos') || "Prosjektbilder"}
                  </Label>
                  <p className="text-gray-500 text-sm">{t('requestProject.files.photosHint') || "Last opp bilder av området/bygget (maks 10MB per bilde)"}</p>
                  
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img src={photo.url} alt={photo.name} className="w-full h-24 object-cover rounded-lg border border-[#2a2a4a]" />
                        <button
                          onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhotos}
                      className="w-full h-24 border-2 border-dashed border-[#2a2a4a] rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-[#23F7DD] hover:text-[#23F7DD] transition-colors"
                    >
                      {uploadingPhotos ? (
                        <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-6 h-6 mb-1" />
                          <span className="text-xs">{t('common.add') || "Legg til"}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Documents Upload */}
                <div className="space-y-3 pt-4 border-t border-[#2a2a4a]">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> {t('requestProject.files.documents') || "Dokumenter"}
                  </Label>
                  <p className="text-gray-500 text-sm">{t('requestProject.files.documentsHint') || "Last opp PDF, tegninger, IFC-filer (maks 50MB)"}</p>
                  
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".pdf,.ifc,.step,.stp,.dwg,.dxf"
                    multiple
                    onChange={handleDocUpload}
                    className="hidden"
                  />
                  
                  <div className="space-y-2">
                    {documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[#23F7DD]" />
                          <div>
                            <p className="text-white text-sm">{doc.name}</p>
                            <p className="text-gray-500 text-xs">{doc.type.toUpperCase()}</p>
                          </div>
                        </div>
                        <button onClick={() => removeDocument(index)} className="text-red-400 hover:text-red-300">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => docInputRef.current?.click()}
                      disabled={uploadingDocs}
                      className="w-full p-4 border-2 border-dashed border-[#2a2a4a] rounded-lg flex items-center justify-center gap-2 text-gray-400 hover:border-[#23F7DD] hover:text-[#23F7DD] transition-colors"
                    >
                      {uploadingDocs ? (
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          <span>{t('requestProject.files.uploadDocs') || "Last opp dokumenter"}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5 - SJA (Safe Job Analysis) - V2 Design */}
            {step === 5 && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#23F7DD] flex items-center justify-center">
                    <Shield className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{t('sja.title')} *</h2>
                    <p className="text-gray-400 text-sm">{t('sja.subtitle')}</p>
                  </div>
                </div>

                {/* Warning Box */}
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-yellow-200 font-medium text-sm">{t('sja.requiredAssessment')}</p>
                    <p className="text-yellow-100/70 text-sm mt-1">
                      {t('sja.requiredAssessmentDesc')}
                    </p>
                  </div>
                </div>

                {/* Job Description */}
                <div>
                  <Label className="text-gray-300 mb-2 block">{t('sja.workDescription')}</Label>
                  <Textarea
                    value={sjaForm.job_description}
                    onChange={(e) => handleSjaChange("job_description", e.target.value)}
                    placeholder={t('sja.workDescriptionPlaceholder')}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white min-h-[100px]"
                  />
                </div>

                {/* Identified Hazards */}
                <div>
                  <Label className="text-gray-300 mb-3 block">{t('sja.hazardsTitle')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {hazardOptions.map((hazard) => (
                      <button
                        key={hazard.id}
                        onClick={() => toggleHazard(hazard.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          isHazardSelected(hazard.id)
                            ? "bg-orange-500 text-white"
                            : "bg-[#2a2a4a] text-gray-300 hover:bg-[#3a3a5a]"
                        }`}
                      >
                        {hazard.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-gray-500 text-sm mt-2">
                    {sjaForm.hazards.length} {t('sja.hazardsRegistered')}
                  </p>
                </div>

                {/* Selected Hazards with Details */}
                {sjaForm.hazards.map((hazard) => {
                  const hazardInfo = hazardOptions.find(h => h.id === hazard.id);
                  const risk = getRiskLevel(hazard.probability, hazard.consequence);
                  return (
                    <Card key={hazard.id} className="bg-[#1a1a2e] border-[#2a2a4a]">
                      <CardContent className="p-4">
                        {/* Hazard Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className="text-white font-medium">{hazardInfo?.label || hazard.id}</span>
                            <Badge className={`${risk.color} text-white text-xs`}>
                              {risk.label}
                            </Badge>
                          </div>
                          <button
                            onClick={() => toggleHazard(hazard.id)}
                            className="text-gray-400 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Probability & Consequence */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <Label className="text-gray-400 text-xs mb-1 block">{t('sja.probability')}</Label>
                            <Select value={hazard.probability} onValueChange={(v) => updateHazardDetails(hazard.id, "probability", v)}>
                              <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                                <SelectItem value="low">{t('sja.low')}</SelectItem>
                                <SelectItem value="medium">{t('sja.medium')}</SelectItem>
                                <SelectItem value="high">{t('sja.high')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-400 text-xs mb-1 block">{t('sja.consequence')}</Label>
                            <Select value={hazard.consequence} onValueChange={(v) => updateHazardDetails(hazard.id, "consequence", v)}>
                              <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                                <SelectItem value="low">{t('sja.low')}</SelectItem>
                                <SelectItem value="medium">{t('sja.medium')}</SelectItem>
                                <SelectItem value="high">{t('sja.high')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Responsible Person */}
                        <div className="mb-4">
                          <Label className="text-gray-400 text-xs mb-1 block">{t('sja.responsiblePerson')}</Label>
                          <Input
                            value={hazard.responsible}
                            onChange={(e) => updateHazardDetails(hazard.id, "responsible", e.target.value)}
                            placeholder={t('sja.responsiblePlaceholder')}
                            className="bg-[#0f0f1a] border-[#2a2a4a] text-white"
                          />
                        </div>

                        {/* Preventive Measures */}
                        <div>
                          <Label className="text-gray-400 text-xs mb-1 block">{t('sja.preventiveMeasures')}</Label>
                          <Textarea
                            value={hazard.measures}
                            onChange={(e) => updateHazardDetails(hazard.id, "measures", e.target.value)}
                            placeholder={t('sja.preventiveMeasuresPlaceholder')}
                            className="bg-[#0f0f1a] border-[#2a2a4a] text-white min-h-[80px]"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Required PPE */}
                <div>
                  <Label className="text-gray-300 mb-3 block">{t('sja.ppeRequired')}</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {ppeOptions.map((ppe) => (
                      <div
                        key={ppe.id}
                        onClick={() => togglePPE(ppe.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all text-center ${
                          sjaForm.ppe_required.includes(ppe.id)
                            ? "bg-[#23F7DD]/20 border-[#23F7DD]"
                            : "bg-[#0f0f1a] border-[#2a2a4a] hover:border-[#3a3a5a]"
                        }`}
                      >
                        <span className="text-2xl block mb-1">{ppe.icon}</span>
                        <span className={`text-xs font-medium ${
                          sjaForm.ppe_required.includes(ppe.id) ? "text-[#23F7DD]" : "text-gray-400"
                        }`}>
                          {ppe.label}
                        </span>
                        {sjaForm.ppe_required.includes(ppe.id) && (
                          <CheckCircle className="w-4 h-4 text-[#23F7DD] mx-auto mt-1" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Safety Measures */}
                <div>
                  <Label className="text-gray-300 mb-2 block">{t('sja.additionalMeasures')}</Label>
                  <Textarea
                    value={sjaForm.additional_measures}
                    onChange={(e) => handleSjaChange("additional_measures", e.target.value)}
                    placeholder={t('sja.additionalMeasuresPlaceholder')}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white"
                  />
                </div>

                {/* Emergency Info */}
                <div className="p-4 bg-[#0f0f1a] border border-[#2a2a4a] rounded-lg">
                  <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    {t('sja.emergencyInfo')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-400 text-xs mb-1 block">{t('sja.emergencyContact')}</Label>
                      <Input
                        value={sjaForm.emergency_contact}
                        onChange={(e) => handleSjaChange("emergency_contact", e.target.value)}
                        placeholder={t('sja.emergencyContactPlaceholder')}
                        className="bg-[#1a1a2e] border-[#2a2a4a] text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs mb-1 block">{t('sja.nearestHospital')}</Label>
                      <Input
                        value={sjaForm.nearest_hospital}
                        onChange={(e) => handleSjaChange("nearest_hospital", e.target.value)}
                        placeholder={t('sja.nearestHospitalPlaceholder')}
                        className="bg-[#1a1a2e] border-[#2a2a4a] text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs mb-1 block">{t('sja.firstAidLocation')}</Label>
                      <Input
                        value={sjaForm.first_aid_location}
                        onChange={(e) => handleSjaChange("first_aid_location", e.target.value)}
                        placeholder={t('sja.firstAidLocationPlaceholder')}
                        className="bg-[#1a1a2e] border-[#2a2a4a] text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6 - Budget (Simplified for Client) */}
            {step === 6 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="w-6 h-6 text-[#23F7DD]" />
                  <div>
                    <h2 className="text-xl font-bold text-white">{t('requestProject.priceInfo.title')}</h2>
                    <p className="text-gray-400 text-sm">{t('requestProject.priceInfo.subtitle')}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-300">{t('requestProject.priceInfo.proposedBudget')} *</Label>
                    <Input 
                      type="number" 
                      min={10000} 
                      step={5000} 
                      value={formData.proposed_budget}
                      onChange={(e) => handleInputChange("proposed_budget", parseFloat(e.target.value) || 0)}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1 text-2xl font-bold h-14" 
                    />
                    <p className="text-gray-500 text-sm mt-2">
                      {t('requestProject.priceInfo.budgetDescription')}
                    </p>
                  </div>

                  {/* Simple Price Display for Client */}
                  <Card className="bg-gradient-to-br from-[#23F7DD]/20 to-[#23F7DD]/5 border-[#23F7DD]/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm uppercase tracking-wider">{t('requestProject.priceInfo.yourTotalPrice')}</p>
                          <p className="text-3xl font-bold text-white mt-1">{formatCurrency(formData.proposed_budget)}</p>
                          <p className="text-gray-400 text-sm mt-1">{t('requestProject.priceInfo.inclAllFees')}</p>
                        </div>
                        <div className="w-16 h-16 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                          <CheckCircle className="w-8 h-8 text-[#23F7DD]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Project Summary */}
                  <Card className="bg-[#0f0f1a] border-[#2a2a4a]">
                    <CardContent className="p-4">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#23F7DD]" />
                        {t('requestProject.summary.title')}
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 text-xs uppercase">{t('requestProject.summary.project')}</span>
                          <p className="text-white">{formData.title || "-"}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs uppercase">{t('requestProject.summary.location')}</span>
                          <p className="text-white">{formData.location_city || "-"}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs uppercase">{t('requestProject.summary.duration')}</span>
                          <p className="text-white">{formData.estimated_duration_days} {t('common.days')}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs uppercase">{t('requestProject.summary.team')}</span>
                          <p className="text-white">{formData.crew_size} {t('requestProject.summary.workers')}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs uppercase">{t('requestProject.summary.photos')}</span>
                          <p className="text-white">{photos.length} {t('requestProject.summary.uploaded')}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs uppercase">{t('requestProject.summary.documents')}</span>
                          <p className="text-white">{documents.length} {t('requestProject.summary.uploaded')}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500 text-xs uppercase">{t('requestProject.summary.sjaStatus')}</span>
                          <p className="text-white">
                            {sjaForm.hazards.length > 0 
                              ? `${sjaForm.hazards.length} ${t('requestProject.summary.hazardsIdentified')}, ${sjaForm.ppe_required.length} ${t('requestProject.summary.ppeSelected')}` 
                              : t('requestProject.summary.notFilled')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Info box */}
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-blue-200 font-medium text-sm">{t('requestProject.priceInfo.whatHappensNext')}</p>
                      <p className="text-blue-100/70 text-sm mt-1">
                        {t('requestProject.priceInfo.whatHappensNextDesc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Nav */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#2a2a4a]">
              {step > 1 ? <Button variant="outline" onClick={() => setStep(step - 1)} className="border-[#2a2a4a] text-white"><ArrowLeft className="w-4 h-4 mr-2" /> {t('requestProject.previous')}</Button> : <div />}
              {step < 6 ? (
                <Button onClick={handleNext} className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black" disabled={!validateStep(step)}>{t('requestProject.next')} <ArrowRight className="w-4 h-4 ml-2" /></Button>
              ) : (
                <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700 text-white" disabled={loading || !validateStep(6)}>{loading ? t('requestProject.submitting') : t('requestProject.submit')} <CheckCircle className="w-4 h-4 ml-2" /></Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 p-4 bg-[#1a1a2e] border border-[#2a2a4a] rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div>
              <h4 className="text-white font-medium">{t('requestProject.whatHappensNext.title')}</h4>
              <ol className="text-gray-400 text-sm mt-2 space-y-1 list-decimal list-inside">
                <li>{t('requestProject.whatHappensNext.step1')}</li>
                <li>{t('requestProject.whatHappensNext.step2')}</li>
                <li>{t('requestProject.whatHappensNext.step3')}</li>
                <li>{t('requestProject.whatHappensNext.step4')}</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
